import React, { useEffect, useMemo } from "react";
import { NumericFormat } from "react-number-format";

// UTILS
import { defaultNumericFormatProps } from "../../../../utils";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { updateOperationMEPData } from "../../../../store/slices/operationMEPSlice";
import { isArgentinaMarketOpen } from "../../utils";

// TYPES
import { OperateSectionProps } from "../../types";

// COMPONENTS
import InputSelect from "../../../InputSelect/InputSelect";
import Button from "../../../Button/Button";
import AmountSelect from "../AmountSelect/AmountSelect";
import Alert from "../../../Alert/Alert";

//STYLES
import styles from "./OperateSection.module.scss";

// ICONS
import InfoIcon from "../../../../assets/icons/info-icon.svg?react";
import InfoRedIcon from "../../../../assets/icons/Info-red-icon.svg?react";

const OperateSection: React.FC<OperateSectionProps> = React.memo(
  ({
    values,
    setFieldValue,
    errors,
    isValid,
    availableArs,
    availableMep,
    handleSubmit,
    handleClose,
    accountOptions,
    handleAccountChange,
    setValues,
    symbolPositionLoading,
    mepPriceToShow,
  }) => {
    const {
      inputNoShadow,
      sideContainer,
      sideButton,
      sideButtonActive,
      priceContainer,
      priceText,
      footer,
      minimumAmount,
      errorText,
      bodyContent,
      header,
      content,
      alertError,
      button,
    } = styles;

    const { isHolidayToday } = useAppSelector((state) => state.marketStatus);
    const { data: operationData } = useAppSelector(
      (state) => state.operationMEPData,
    );
    const isMarketOpen = isArgentinaMarketOpen() && !isHolidayToday;
    const dispatch = useAppDispatch();

    useEffect(() => {
      dispatch(
        updateOperationMEPData({ ...operationData, isDetailsView: false }),
      );
    }, []);

    // -------- Aux components -------- //

    const isBuySide = values.side === "BUY";

    const AccountSelector = useMemo(
      () => (
        <InputSelect
          key="account-select-operate-modal"
          items={accountOptions ?? []}
          name="account"
          type="single"
          label={values.account ? null : "Cuenta Comitente"}
          placeholder="Seleccionar"
          isDisabled={accountOptions.length === 1}
          inputValue={values.account}
          onChange={(key) => handleAccountChange(Array.isArray(key) ? key[0] : key)}
          className={inputNoShadow}
          aria-label="Cuentas"
          classNames={{
            base: "opacity-1",
            trigger:
              "data-[disabled=true]:!bg-[#D9D9D9] data-[disabled=true]:!border-[#D9D9D9]",
          }}
        />
      ),
      [accountOptions, values.account, handleAccountChange],
    );

    const SideSelector = useMemo(
      () => (
        <div className={sideContainer}>
          <div
            onClick={() => {
              setFieldValue("side", "BUY");
              setFieldValue("operationValueArs", "");
              setFieldValue("operationValueMep", "");
              setFieldValue("isTotal", false);
            }}
            className={`${sideButton} ${isBuySide ? sideButtonActive : ""}`}
          >
            Comprar
          </div>
          <div
            onClick={() => {
              setFieldValue("side", "SELL");
              setFieldValue("operationValueArs", "");
              setFieldValue("operationValueMep", "");
              setFieldValue("isTotal", false);
            }}
            className={`${sideButton} ${!isBuySide ? sideButtonActive : ""}`}
          >
            Vender
          </div>
        </div>
      ),
      [
        values.side,
        values.isTotal,
        values.operationValueArs,
        values.operationValueMep,
      ],
    );

    const Price = useMemo(
      () => (
        <div className={priceContainer}>
          <span className={priceText}>
            Estás {isBuySide ? "comprando" : "vendiendo"} a $
            <b>
              <NumericFormat
                {...defaultNumericFormatProps}
                prefix=""
                value={isBuySide ? mepPriceToShow : mepPriceToShow}
              />
            </b>
          </span>
        </div>
      ),
      [values.side, mepPriceToShow, mepPriceToShow],
    );

    const AmountSelector = useMemo(
      () => (
        <AmountSelect
          setFieldValue={setFieldValue}
          setValues={setValues}
          isBuyingSide={isBuySide}
          operationValueArs={values.operationValueArs}
          operationValueMep={values.operationValueMep}
          isTotal={values.isTotal}
          availableArs={availableArs.toString()}
          availableMep={availableMep.toString()}
          symbolPositionLoading={symbolPositionLoading}
        />
      ),
      [
        values.operationValueArs,
        values.operationValueMep,
        values.isTotal,
        availableArs,
        availableMep,
        isBuySide,
        setFieldValue,
        setValues,
        symbolPositionLoading,
      ],
    );

    const getInfoText = () => {
      if (!!values?.operationValueArs && !!errors.operationValueArs)
        return errors.operationValueArs;
      if (!!values?.operationValueMep && !!errors.operationValueMep)
        return errors.operationValueMep;
      return isBuySide
        ? "Podés comprar hasta 50.000 dólares MEP. "
        : "El monto mínimo es 1 dólar MEP.";
    };

    const showErrorText =
      !isValid && !!values?.operationValueArs && !!values?.operationValueMep;

    const MinimumAmountText = useMemo(
      () => (
        <div>
          <p className={`${minimumAmount} ${showErrorText ? errorText : ""}`}>
            {showErrorText ? <InfoRedIcon /> : <InfoIcon />}
            {getInfoText()}
          </p>
        </div>
      ),
      [
        showErrorText,
        values.operationValueArs,
        values.operationValueMep,
        isBuySide,
        errors,
      ],
    );

    const ActionButtons = useMemo(
      () => (
        <div className={footer}>
          <Button
            text="Cancelar"
            variant="secondary"
            onPress={handleClose}
            className={button}
          />
          <Button
            text="Continuar"
            onPress={() => handleSubmit()}
            isDisabled={
              !isValid ||
              !values.operationValueArs ||
              !values.operationValueMep ||
              !isMarketOpen
            }
            className={button}
          />
        </div>
      ),
      [
        isValid,
        handleSubmit,
        handleClose,
        footer,
        values.operationValueArs,
        values.operationValueMep,
      ],
    );

    return (
      <div className={content}>
        <div className={header}>Operar</div>
        {!isMarketOpen && (
          <Alert variant="ERROR" className={alertError}>
            El mercado está cerrado. Podés operar de 10:45 a 16:30.
          </Alert>
        )}
        <div className={bodyContent}>
          {AccountSelector}
          {SideSelector}
          {Price}
          {AmountSelector}
          {MinimumAmountText}
        </div>
        {ActionButtons}
      </div>
    );
  },
);

export default OperateSection;
